// Tìm nhanh (Ctrl/Cmd + K) — port src/shell/CommandPalette.jsx: đi tới module, chạy lệnh tạo mới, tìm đối tượng do từng
// module cung cấp (SearchProvider, hoãn 200 ms, mỗi nguồn một truy vấn độc lập — lỗi nguồn này không chặn nguồn khác).
// Kết quả tôn trọng quyền xem vì mỗi service tự lọc theo phạm vi. Chỉ liệt kê (và cho Enter mở) kết quả của đúng chuỗi
// đang gõ: trong lúc hoãn / đang tải, nhóm của nguồn vắng mặt và chân hộp báo "Đang tìm…".
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { qk } from '@/shared/api';
import { useDebounced, useOverlay } from '@/shared/lib';
import { Icon, useDialogFocus, useEscapeLayer } from '@/shared/ui';
import type { ModuleDef } from '@/entities/tenant';
import { useViewer } from '@/entities/viewer';
import { DEFAULT_MIN_LENGTH, SEARCH_DEBOUNCE_MS, paletteEntries, type CreateItem, type PaletteEntry, type SearchProvider } from './model';

/** Mở bằng Ctrl/Cmd + K hoặc sự kiện window 'cmdk' (nút tìm trên thanh trên, menu tài khoản). */
export function CommandPalette({ modules, createItems, providers }: { modules: ModuleDef[]; createItems: CreateItem[]; providers: SearchProvider[] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const ev = () => setOpen(true);
    window.addEventListener('keydown', key);
    window.addEventListener('cmdk', ev);
    return () => {
      window.removeEventListener('keydown', key);
      window.removeEventListener('cmdk', ev);
    };
  }, []);
  if (!open) return null;
  return <PaletteDialog modules={modules} createItems={createItems} providers={providers} onClose={() => setOpen(false)} />;
}

function PaletteDialog({ modules, createItems, providers, onClose }: { modules: ModuleDef[]; createItems: CreateItem[]; providers: SearchProvider[]; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const dialog = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const overlay = useOverlay();
  const b = useViewer();
  const visible = useCallback((id: string) => b.visibleModules.includes(id), [b.visibleModules]);
  // Esc đóng Tìm nhanh (lớp trên cùng), không đóng drawer/modal bên dưới. Tiêu điểm như hộp thoại: ô nhập nhận tiêu điểm ngay
  // khi gắn (autoFocus — phím gõ liền sau Ctrl K không bị mất), bẫy Tab trong hộp, đóng ⇒ trả về phần tử đã mở (drawer mở từ
  // một kết quả nhận lại phần tử đó làm nơi trả tiêu điểm)
  useEscapeLayer(onClose);
  useDialogFocus(dialog);
  const term = q.trim();
  const dq = useDebounced(term, SEARCH_DEBOUNCE_MS);
  const min = (p: SearchProvider) => p.minLength ?? DEFAULT_MIN_LENGTH;
  const results = useQueries({
    queries: providers.map((p) => ({
      queryKey: qk.search.q(p.id, dq),
      queryFn: ({ signal }: { signal: AbortSignal }) => p.search(dq, { signal, visible }),
      enabled: dq.length >= min(p),
      staleTime: 30_000,
      retry: false,
    })),
  });
  // Kết quả nguồn chỉ khi khớp đúng chuỗi đang gõ (đã qua thời gian hoãn) — không để kết quả của chuỗi cũ nằm dưới chuỗi
  // mới (Enter sẽ mở nhầm).
  const settled = dq === term;
  const ready = settled ? providers.flatMap((provider, k) => (dq.length >= min(provider) && results[k]?.data ? [{ provider, hits: results[k]!.data! }] : [])) : [];
  const list = paletteEntries(q, modules, createItems, ready);
  const searching = !!term && providers.some((p, k) => term.length >= min(p) && (!settled || (results[k]?.isFetching ?? false)));
  const sel = Math.min(i, Math.max(0, list.length - 1));
  useEffect(() => {
    listRef.current?.querySelector(`#cmdk-${sel}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [sel]);

  const run = (r: PaletteEntry | undefined) => {
    if (!r) return;
    onClose();
    if (r.overlay) overlay.open(r.overlay[0], r.overlay[1]);
    else if (r.to) void navigate({ to: r.to, search: r.search as never });
  };
  let lastGroup = '';
  return (
    <div className="cmdk-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} className="cmdk" role="dialog" aria-modal="true" aria-label="Tìm nhanh">
        <div className="cmdk-input">
          <Icon n="search" s={17} />
          <input
            id="cmdk-q"
            autoFocus
            value={q}
            autoComplete="off"
            placeholder="Tìm khách hàng, mã căn, mã giao dịch, nhân sự, hoặc gõ tên mục…"
            onChange={(e) => {
              setQ(e.currentTarget.value);
              setI(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setI(Math.min(sel + 1, list.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setI(Math.max(sel - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                run(list[sel]);
              }
            }}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={list.length ? `cmdk-${sel}` : undefined}
            aria-label="Tìm nhanh"
          />
          <kbd aria-hidden="true">Esc</kbd>
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox" ref={listRef}>
          {!list.length && <div className="cmdk-empty">{searching ? 'Đang tìm…' : `Không tìm thấy kết quả cho “${q}”.`}</div>}
          {list.map((r, k) => {
            const head = r.group !== lastGroup;
            lastGroup = r.group;
            return (
              <Fragment key={r.id}>
                {head && <div className="cmdk-group">{r.group}</div>}
                <button type="button" id={`cmdk-${k}`} role="option" aria-selected={k === sel} className={`cmdk-item ${k === sel ? 'on' : ''}`} onMouseEnter={() => setI(k)} onClick={() => run(r)}>
                  <Icon n={r.icon || 'right'} s={15} />
                  <span className="grow">
                    <span className="t">{r.title}</span>
                    {r.sub && <span className="s">{r.sub}</span>}
                  </span>
                  {r.meta && <span className="m">{r.meta}</span>}
                </button>
              </Fragment>
            );
          })}
        </div>
        <div className="cmdk-foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> chọn
          </span>
          <span>
            <kbd>Enter</kbd> mở
          </span>
          {searching && list.length > 0 && (
            <span className="grow" style={{ textAlign: 'right' }} role="status">
              Đang tìm…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
